import React, { useState, useEffect } from "react";
import { 
    Col, Row, Card, CardBody, CardHeader, Container, Button, 
    Modal, ModalHeader, ModalBody, ModalFooter, Alert, Form, FormGroup, Label, Input
} from "reactstrap";
import Breadcrumbs from "../../../Components/Common/Breadcrumb";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import TableContainer from "Components/Common/TableContainer";

const baseUrl = process.env.REACT_APP_BASE_URL;

interface RAGDocument {
    id: string;
    title: string;
    description: string;
    file: string;
    file_name: string;
    file_size_mb: number;
    file_extension: string;
    uploaded_by_name: string;
    uploaded_at: string;
    domain_name: string;
}

interface Agent {
    id: string;
    display_name: string;
    description: string;
    agent_type: string;
    is_active: boolean;
    available: boolean;
}

interface column {
    header: string;
    accessorKey?: string;
    accessorFn?: (row: any) => string;
    enableColumnFilter: boolean;
    enableSorting: boolean;
    cell?: (info: any) => React.ReactNode;
}

const RAGDocumentsManagement = () => {
    const [searchParams] = useSearchParams();
    const domainId = searchParams.get('domain');
    
    const [documents, setDocuments] = useState<RAGDocument[]>([]);
    const [domainName, setDomainName] = useState<string>('');
    const [showloader, setshowloader] = useState(true);
    const [alertMsgg, setAlertMsgg] = useState<any>({ open: false, type: "success", content: 'Messages here.', time: '2500' });
    
    // Agent-related state
    const [currentAgent, setCurrentAgent] = useState<Agent | null>(null);
    const [availableAgents, setAvailableAgents] = useState<Agent[]>([]);
    const [agentModal, setAgentModal] = useState(false);
    const [selectedAgentId, setSelectedAgentId] = useState<string>('');
    
    // Modal states
    const [uploadModal, setUploadModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<RAGDocument | null>(null);
    
    // Upload form states
    const [uploadForm, setUploadForm] = useState({
        title: '',
        description: '',
        file: null as File | null
    });

    const handleAlertMsggClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") { return; }
        setAlertMsgg({ ...alertMsgg, open: false });
    };



    const fetchDocuments = async () => {
        if (!domainId) return;
        
        try {
            setshowloader(true);
            const response = await axios.get(
                `/chatbot/rag-documents/?domain=${domainId}`,
                { withCredentials: true }
            );
            
            if (response.status === "SENT") {
                setDocuments(response.documents || []);
                // Set domain name from the API response
                if (response.domain_name) {
                    setDomainName(response.domain_name);
                }
                // Set current agent from the API response
                if (response.agent) {
                    setCurrentAgent(response.agent);
                } else {
                    setCurrentAgent(null);
                }
            }
            setshowloader(false);
        } catch (error: any) {
            console.error('[RAGDocumentsManagement] fetchDocuments failed', {
                domainId,
                error,
                message: error?.message,
                status: error?.response?.status,
                response: error?.response?.data
            });
            if (error.response) {
                if ([401, 422].includes(error.response.status)) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: error.response.data?.error || "Error occurred", type: "error" });
                } else if (error.response.status === 403) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Session expired!", type: "error" });
                    setTimeout(() => { window.location.href = "/logout"; }, 2000);
                } else {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "An error occurred", type: "error" });
                }
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
            setshowloader(false);
        }
    };

    const handleUpload = async () => {
        if (!domainId || !uploadForm.file) return;
        
        try {
            setshowloader(true);
            const formData = new FormData();
            formData.append('domain', domainId);
            formData.append('title', uploadForm.title || uploadForm.file.name);
            formData.append('description', uploadForm.description);
            formData.append('file', uploadForm.file);
            
            const response = await axios.post(
                `/chatbot/rag-documents/`,
                formData,
                { 
                    withCredentials: true,
                    headers: { 'Content-Type': 'multipart/form-data' }
                }
            );
            
            if (response.status === "CREATED") {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Document uploaded successfully!", type: "success" });
                setUploadModal(false);
                setUploadForm({ title: '', description: '', file: null });
                fetchDocuments();
            }
            setshowloader(false);
        } catch (error: any) {
            console.error('[RAGDocumentsManagement] handleUpload failed', {
                domainId,
                title: uploadForm.title,
                fileName: uploadForm.file?.name,
                error,
                message: error?.message,
                status: error?.response?.status,
                response: error?.response?.data
            });
            if (error.response) {
                if ([401, 422].includes(error.response.status)) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: error.response.data?.error || "Error occurred", type: "error" });
                } else if (error.response.status === 403) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Session expired!", type: "error" });
                    setTimeout(() => { window.location.href = "/logout"; }, 2000);
                } else {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "An error occurred", type: "error" });
                }
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
            setshowloader(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedDocument) return;
        
        try {
            setshowloader(true);
            const response = await axios.delete(
                `/chatbot/rag-documents/${selectedDocument.id}/`,
                { withCredentials: true }
            );
            
            if (response.status === "DELETED") {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Document deleted successfully!", type: "success" });
                setDeleteModal(false);
                setSelectedDocument(null);
                fetchDocuments();
            }
            setshowloader(false);
        } catch (error: any) {
            console.error('[RAGDocumentsManagement] handleDelete failed', {
                documentId: selectedDocument?.id,
                error,
                message: error?.message,
                status: error?.response?.status,
                response: error?.response?.data
            });
            if (error.response) {
                if ([401, 422].includes(error.response.status)) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: error.response.data?.error || "Error occurred", type: "error" });
                } else if (error.response.status === 403) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Session expired!", type: "error" });
                    setTimeout(() => { window.location.href = "/logout"; }, 2000);
                } else {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "An error occurred", type: "error" });
                }
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
            setshowloader(false);
        }
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    // Agent management functions
    const fetchAvailableAgents = async () => {
        try {
            const response = await axios.get('/chatbot/agents/', { withCredentials: true });
            if (response.status === "SENT") {
                setAvailableAgents(response.agents || []);
            }
        } catch (error: any) {
            console.error('[RAGDocumentsManagement] fetchAvailableAgents failed', {
                error,
                message: error?.message,
                status: error?.response?.status,
                response: error?.response?.data
            });
        }
    };

    const handleAgentAssignment = async () => {
        if (!domainId) return;
        
        try {
            setshowloader(true);
            const response = await axios.post(
                `/chatbot/domain-agent-assignment/${domainId}/`,
                { agent_id: selectedAgentId || null },
                { withCredentials: true }
            );
            
            if (response.status === "ASSIGNED") {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Agent assignment updated successfully!", type: "success" });
                setAgentModal(false);
                setSelectedAgentId('');
                fetchDocuments(); // Refresh to get updated agent info
            }
            setshowloader(false);
        } catch (error: any) {
            console.error('[RAGDocumentsManagement] handleAgentAssignment failed', {
                domainId,
                agentId: selectedAgentId,
                error,
                message: error?.message,
                status: error?.response?.status,
                response: error?.response?.data
            });
            if (error.response) {
                setAlertMsgg({ ...alertMsgg, open: true, content: error.response.data?.error || "Error occurred", type: "error" });
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
            setshowloader(false);
        }
    };

    const handleUnassignAgent = async () => {
        if (!domainId) return;
        
        try {
            setshowloader(true);
            const response = await axios.delete(
                `/chatbot/unassign-domain-agent/${domainId}/`,
                { withCredentials: true }
            );
            
            if (response.status === "UNASSIGNED") {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Agent unassigned successfully!", type: "success" });
                setCurrentAgent(null);
                fetchDocuments(); // Refresh to get updated agent info
            }
            setshowloader(false);
        } catch (error: any) {
            console.error('[RAGDocumentsManagement] handleUnassignAgent failed', {
                domainId,
                error,
                message: error?.message,
                status: error?.response?.status,
                response: error?.response?.data
            });
            if (error.response) {
                setAlertMsgg({ ...alertMsgg, open: true, content: error.response.data?.error || "Error occurred", type: "error" });
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
            setshowloader(false);
        }
    };

    useEffect(() => {
        fetchDocuments();
        fetchAvailableAgents();
    }, [domainId]);

    const columns: column[] = [
        {
            header: 'S.No',
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }) => row.index + 1,
        },
        {
            header: 'Title',
            enableColumnFilter: true,
            enableSorting: true,
            cell: ({ row }) => (
                <div className="fw-medium text-capitalize">{row.original.title}</div>
            ),
        },
        {
            header: 'File Name',
            enableColumnFilter: true,
            enableSorting: true,
            cell: ({ row }) => (
                <div className="font-size-12 text-muted">{row.original.file_name}</div>
            ),
        },
        {
            header: 'Size',
            enableColumnFilter: false,
            enableSorting: true,
            cell: ({ row }) => (
                <div className="fw-medium">{row.original.file_size_mb} MB</div>
            ),
        },
        {
            header: 'Type',
            enableColumnFilter: false,
            enableSorting: true,
            cell: ({ row }) => (
                <div className="fw-medium text-uppercase">{row.original.file_extension}</div>
            ),
        },
        {
            header: 'Uploaded By',
            enableColumnFilter: true,
            enableSorting: true,
            cell: ({ row }) => (
                <div className="fw-medium">{row.original.uploaded_by_name}</div>
            ),
        },
        {
            header: 'Uploaded At',
            enableColumnFilter: false,
            enableSorting: true,
            cell: ({ row }) => (
                <div className="font-size-12">{formatDate(row.original.uploaded_at)}</div>
            ),
        },
        {
            header: 'Actions',
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }) => (
                <div className="d-flex gap-2">
                    <Button 
                        color="primary" 
                        className="btn btn-sm"
                        onClick={() => {
                            if (row.original.file) {
                                window.open(`${baseUrl}${row.original.file}`, '_blank');
                            }
                        }}
                    >
                        <i className="far fa-eye me-1"></i> View
                    </Button>
                    <Button 
                        color="danger" 
                        className="btn btn-sm"
                        onClick={() => {
                            setSelectedDocument(row.original);
                            setDeleteModal(true);
                        }}
                    >
                        <i className="far fa-trash-alt me-1"></i> Delete
                    </Button>
                </div>
            ),
        }
    ];

    if (!domainId) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Admin" breadcrumbItem="RAG Documents" />
                    <Alert color="warning">
                        No domain selected. Please select a domain to manage RAG documents.
                    </Alert>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs title="Admin" breadcrumbItem="RAG Documents" />
                
                {alertMsgg.open && (
                    <Alert
                        color={alertMsgg.type === "success" ? "success" : "danger"}
                        toggle={handleAlertMsggClose}
                        fade={false}
                        style={{ fontWeight: "bold" }}
                    >
                        {alertMsgg.content}
                    </Alert>
                )}
                
                <div className="loaderContainer" hidden={!showloader}>
                    <div className="spinner">
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                    </div>
                </div>

                {/* Agent Assignment Section */}
                <Row className="mb-4">
                    <Col lg={12}>
                        <Card>
                            <CardHeader>
                                <h5 className="card-title mb-0">Agent Assignment</h5>
                                <small className="text-muted">Assign an AI agent to handle queries for this domain</small>
                            </CardHeader>
                            <CardBody>
                                <Row className="align-items-center">
                                    <Col md={8}>
                                        {currentAgent ? (
                                            <div className="d-flex align-items-center">
                                                <div className="me-3">
                                                    <i className="bx bx-robot text-primary" style={{ fontSize: '2rem' }}></i>
                                                </div>
                                                <div>
                                                    <h6 className="mb-1">{currentAgent.display_name}</h6>
                                                    <p className="text-muted mb-0">{currentAgent.description}</p>
                                                    <small className="text-success">
                                                        <i className="bx bx-check-circle me-1"></i>
                                                        Active Agent
                                                    </small>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="d-flex align-items-center">
                                                <div className="me-3">
                                                    <i className="bx bx-x-circle text-muted" style={{ fontSize: '2rem' }}></i>
                                                </div>
                                                <div>
                                                    <h6 className="mb-1 text-muted">No Agent Assigned</h6>
                                                    <p className="text-muted mb-0">This domain doesn't have an AI agent assigned</p>
                                                </div>
                                            </div>
                                        )}
                                    </Col>
                                    <Col md={4} className="text-end">
                                        <Button
                                            color="primary"
                                            onClick={() => {
                                                setSelectedAgentId(currentAgent?.id || '');
                                                setAgentModal(true);
                                            }}
                                            className="me-2"
                                        >
                                            <i className="bx bx-edit me-1"></i>
                                            {currentAgent ? 'Change Agent' : 'Assign Agent'}
                                        </Button>
                                        {currentAgent && (
                                            <Button
                                                color="outline-danger"
                                                onClick={handleUnassignAgent}
                                            >
                                                <i className="bx bx-x me-1"></i>
                                                Unassign
                                            </Button>
                                        )}
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h4 className="card-title mb-0">RAG Documents</h4>
                                    {domainName && (
                                        <small className="text-muted text-capitalize">Domain: {domainName}</small>
                                    )}
                                </div>
                                <Button 
                                    color="primary" 
                                    onClick={() => setUploadModal(true)}
                                >
                                    <i className="bx bx-plus me-1"></i> Upload Document
                                </Button>
                            </CardHeader>
                            <CardBody>
                                <TableContainer
                                    columns={columns}
                                    data={documents}
                                    isGlobalFilter={true}
                                    isPagination={true}
                                    SearchPlaceholder="Search documents..."
                                    pagination="pagination"
                                    paginationWrapper='dataTables_paginate paging_simple_numbers'
                                    tableClass="table-bordered dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Upload Modal */}
                <Modal isOpen={uploadModal} toggle={() => setUploadModal(!uploadModal)} size="lg">
                    <ModalHeader toggle={() => setUploadModal(!uploadModal)}>
                        Upload RAG Document
                    </ModalHeader>
                    <ModalBody>
                        <Form>
                            <FormGroup>
                                <Label for="title">Title (Optional)</Label>
                                <Input
                                    id="title"
                                    type="text"
                                    value={uploadForm.title}
                                    onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                                    placeholder="Enter document title"
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label for="description">Description (Optional)</Label>
                                <Input
                                    id="description"
                                    type="textarea"
                                    value={uploadForm.description}
                                    onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                                    placeholder="Enter document description"
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label for="file">File</Label>
                                <Input
                                    id="file"
                                    type="file"
                                    onChange={(e) => setUploadForm({...uploadForm, file: e.target.files?.[0] || null})}
                                    accept=".pdf,.doc,.docx,.txt,.rtf"
                                />
                                <small className="text-muted">
                                    Supported formats: PDF, DOC, DOCX, TXT, RTF
                                </small>
                            </FormGroup>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setUploadModal(false)}>
                            Cancel
                        </Button>
                        <Button 
                            color="primary" 
                            onClick={handleUpload}
                            disabled={!uploadForm.file}
                        >
                            Upload
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)}>
                    <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>
                        Confirm Delete
                    </ModalHeader>
                    <ModalBody>
                        Are you sure you want to delete "{selectedDocument?.title}"?
                        This action cannot be undone.
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button color="danger" onClick={handleDelete}>
                            Delete
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Agent Assignment Modal */}
                <Modal isOpen={agentModal} toggle={() => setAgentModal(!agentModal)} size="lg">
                    <ModalHeader toggle={() => setAgentModal(!agentModal)}>
                        {currentAgent ? 'Change Agent Assignment' : 'Assign Agent to Domain'}
                    </ModalHeader>
                    <ModalBody>
                        <Form>
                            <FormGroup>
                                <Label for="agentSelect">Select Agent</Label>
                                <Input
                                    id="agentSelect"
                                    type="select"
                                    value={selectedAgentId}
                                    onChange={(e) => setSelectedAgentId(e.target.value)}
                                >
                                    <option value="">No Agent (Unassigned)</option>
                                    {availableAgents.map((agent) => (
                                        <option key={agent.id} value={agent.id}>
                                            {agent.display_name} - {agent.description}
                                        </option>
                                    ))}
                                </Input>
                                <small className="text-muted">
                                    Choose an agent to handle queries for this domain, or select "No Agent" to unassign.
                                </small>
                            </FormGroup>
                            
                            {selectedAgentId && (
                                <div className="mt-3 p-3 bg-light rounded">
                                    {(() => {
                                        const selectedAgent = availableAgents.find(a => a.id === selectedAgentId);
                                        return selectedAgent ? (
                                            <div>
                                                <h6 className="mb-2">{selectedAgent.display_name}</h6>
                                                <p className="text-muted mb-0">{selectedAgent.description}</p>
                                                <small className="text-success">
                                                    <i className="bx bx-check-circle me-1"></i>
                                                    Agent is active and ready
                                                </small>
                                            </div>
                                        ) : null;
                                    })()}
                                </div>
                            )}
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setAgentModal(false)}>
                            Cancel
                        </Button>
                        <Button color="primary" onClick={handleAgentAssignment}>
                            {currentAgent ? 'Update Assignment' : 'Assign Agent'}
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
};

export default RAGDocumentsManagement; 